import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent, MouseEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CUSTOM_REPORTS,
  CUSTOM_REPORT_GROUPS,
  SAVED_DATE_PRESETS,
  SAVED_VIEW_TYPES,
  SCHEDULE_FREQUENCIES,
  customReportCodes,
  customReportSections,
  customReportSource,
  getCustomReport,
  liveColumns,
} from '@app/shared';
import type {
  CustomReport,
  SavedReportDto,
  SavedReportInput,
  ScheduleTaskInput,
  ScheduledTaskDto,
} from '@app/shared';
import type { AgentChain } from '../../components/AgentChainSelect';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { ConfirmDialog, FormDialog } from '../../components/dialogs';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ApiError, api } from '../../lib/api';
import type { Query } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useAuth } from '../auth/AuthProvider';
import { RequirePermission } from '../auth/guards';
import { ReleasedFeature, useFeatureReleased } from '../../release';
import { ReportFilterFields, filtersToQuery } from './FilterFields';
import type { FilterDraft } from './FilterFields';

/** Field errors of a failed request, by field name. */
const fieldErrors = (err: unknown): Record<string, string> =>
  err instanceof ApiError ? Object.fromEntries(err.details.map((d) => [d.path, d.message])) : {};

/* ------------------------------ Customize Reports -------------------------- */

/**
 * Customize Reports (PC429), "Customizable Reports": the builders in the reference's four
 * groups. Only builders this application can fill are listed, and only those the user holds
 * the codes for.
 */
export function CustomizeReportsPage() {
  const { can } = useAuth();
  const featureOn = useFeatureReleased();
  // A builder added after a deployment is offered once its release switch is on.
  const open = CUSTOM_REPORTS.filter(
    (r) => customReportCodes(r).every(can) && (!r.feature || featureOn(r.feature)),
  );
  return (
    <Box>
      <PageHeader title="Customizable Reports" code="PC429" />
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' } }}
      >
        {CUSTOM_REPORT_GROUPS.map(([group, title]) => {
          const reports = open.filter((r) => r.group === group);
          return (
            <Paper
              key={group}
              variant="outlined"
              component="section"
              aria-label={title}
              sx={{ p: 2 }}
            >
              <Typography variant="h3" sx={{ mb: 1 }}>
                {title}
              </Typography>
              {reports.length === 0 ? (
                <Typography color="text.secondary">No report available to you.</Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.75 }}>
                  {reports.map((r) => (
                    <li key={r.key}>
                      <MuiLink component={Link} to={`/reports/customize/${r.key}`}>
                        {r.name}
                      </MuiLink>
                    </li>
                  ))}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

/** "Saved Parameters": Save as, View type, Schedule date. */
function SaveSelectionDialog({
  report,
  columns,
  filters,
  onClose,
}: {
  report: CustomReport;
  columns: string[];
  filters: Record<string, string>;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [viewType, setViewType] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (input: SavedReportInput) =>
      api.post<SavedReportDto>('/custom-reports/saved', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['custom-reports'] }),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!name.trim()) found.name = 'Save as is required';
    if (!viewType) found.viewType = 'Select View type';
    if (!datePreset) found.datePreset = 'Select Schedule date';
    setProblems(found);
    if (Object.keys(found).length) return;
    try {
      const saved = await save.mutateAsync({
        reportKey: report.key,
        name,
        viewType: viewType as SavedReportInput['viewType'],
        datePreset: datePreset as SavedReportInput['datePreset'],
        columns,
        filters,
      });
      notify.success(`"${saved.name}" saved. It is listed under Saved report settings.`);
      onClose();
    } catch (err) {
      const fromServer = fieldErrors(err);
      if (Object.keys(fromServer).length) setProblems(fromServer);
      else notify.error(err);
    }
  };

  return (
    <FormDialog
      open
      title="Saved Parameters"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <TextField
          required
          autoFocus
          label="Save as"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={Boolean(problems.name)}
          helperText={problems.name}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          select
          required
          label="View type"
          value={viewType}
          onChange={(e) => setViewType(e.target.value)}
          error={Boolean(problems.viewType)}
          helperText={problems.viewType ?? 'Public: every user can run it. Private: only you.'}
        >
          {SAVED_VIEW_TYPES.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          required
          label="Schedule date"
          value={datePreset}
          onChange={(e) => setDatePreset(e.target.value)}
          error={Boolean(problems.datePreset)}
          helperText={problems.datePreset ?? 'The dates the report covers each time it runs.'}
        >
          {SAVED_DATE_PRESETS.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </FormDialog>
  );
}

function Builder({ report }: { report: CustomReport }) {
  const def = customReportSource(report);
  const featureOn = useFeatureReleased();
  // A column behind a release switch is offered only while the switch is on.
  const sections = customReportSections(report)
    .map((s) => ({ ...s, columns: liveColumns(s.columns, featureOn) }))
    .filter((s) => s.columns.length > 0);
  const [draft, setDraft] = useState<FilterDraft>({});
  const [chain, setChain] = useState<AgentChain>({});
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const { query, problems: filterProblems } = filtersToQuery(def, draft, chain);
  const columns = def.columns.filter((c) => checked.has(c.key)).map((c) => c.key);
  const runQuery: Query = { ...query, columns: columns.join(',') };

  /** Shows what is missing; true when the builder can run. */
  const validate = (): boolean => {
    const found = { ...filterProblems };
    if (columns.length === 0) found.columns = 'Select at least one output column';
    setProblems(found);
    return Object.keys(found).length === 0;
  };

  const toggle = (keys: string[], on: boolean) =>
    setChecked((current) => {
      const next = new Set(current);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });

  // What "Save My Selection" keeps: the search parameters without the dates.
  const savedFilters = Object.fromEntries(
    Object.entries(query)
      .filter(
        ([key, value]) => typeof value === 'string' && value !== '' && !/(From|To)$/.test(key),
      )
      .map(([key, value]) => [key, String(value)]),
  );

  return (
    <Box>
      <PageHeader title={report.name} code="PC429" />
      {def.filters.length ? (
        <Paper
          variant="outlined"
          component="section"
          aria-label="Search Parameter"
          sx={{ p: 2, mb: 1.5 }}
        >
          <Typography variant="h3" sx={{ mb: 1.5 }}>
            Search Parameter
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
              alignItems: 'start',
            }}
          >
            <ReportFilterFields
              def={def}
              draft={draft}
              onChange={(key, value) => {
                setDraft((d) => ({ ...d, [key]: value }));
                setProblems((p) => ({ ...p, [key]: '' }));
              }}
              chain={chain}
              onChainChange={setChain}
              problem={(key) => problems[key] ?? ''}
            />
          </Box>
        </Paper>
      ) : null}

      {sections.map((section) => {
        const keys = section.columns.map((c) => c.key);
        const all = keys.every((k) => checked.has(k));
        return (
          <Paper
            key={section.title}
            variant="outlined"
            component="section"
            aria-label={section.title}
            sx={{ p: 2, mb: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Typography variant="h3">{section.title}</Typography>
              <FormControlLabel
                label="Select All"
                control={
                  <Checkbox
                    size="small"
                    checked={all}
                    indeterminate={!all && keys.some((k) => checked.has(k))}
                    onChange={(e) => toggle(keys, e.target.checked)}
                  />
                }
              />
            </Box>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}
            >
              {section.columns.map((c) => (
                <FormControlLabel
                  key={c.key}
                  label={c.header}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked.has(c.key)}
                      onChange={(e) => toggle([c.key], e.target.checked)}
                    />
                  }
                />
              ))}
            </Box>
          </Paper>
        );
      })}

      {problems.columns ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {problems.columns}
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography>Output Type: CSV</Typography>
        <Button
          component="a"
          href={api.url(`/custom-reports/run/${report.key}`, runQuery)}
          download
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={(e: MouseEvent) => {
            if (!validate()) e.preventDefault();
          }}
        >
          Run Report
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            if (validate()) setSaving(true);
          }}
        >
          Save My Selection
        </Button>
      </Box>
      {saving ? (
        <SaveSelectionDialog
          report={report}
          columns={columns}
          filters={savedFilters}
          onClose={() => setSaving(false)}
        />
      ) : null}
    </Box>
  );
}

/** One builder of Customize Reports: search parameters, output columns, CSV. */
export function CustomReportBuilderPage() {
  const { key } = useParams();
  const report = getCustomReport(key ?? '');
  if (!report) return <Alert severity="warning">This report does not exist.</Alert>;
  const builder = (
    <RequirePermission code="PC429">
      <RequirePermission code={customReportSource(report).code}>
        {/* `key` gives each builder its own selection. */}
        <Builder key={report.key} report={report} />
      </RequirePermission>
    </RequirePermission>
  );
  return report.feature ? (
    <ReleasedFeature name={report.feature} page>
      {builder}
    </ReleasedFeature>
  ) : (
    builder
  );
}

/* ---------------------------- Saved report settings ------------------------ */

const useSaved = (params: Query) =>
  useQuery({
    queryKey: ['custom-reports', 'saved', params],
    queryFn: () => api.get<{ data: SavedReportDto[] }>('/custom-reports/saved', params),
    select: (r) => r.data,
    placeholderData: (prev) => prev,
  });

/** "Schedule Task" for a saved report. */
function ScheduleDialog({ saved, onClose }: { saved: SavedReportDto; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [name, setName] = useState(saved.name);
  const [frequency, setFrequency] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const schedule = useMutation({
    mutationFn: (input: ScheduleTaskInput) =>
      api.post<ScheduledTaskDto>('/custom-reports/scheduled', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['custom-reports'] }),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!name.trim()) found.name = 'Report Name is required';
    if (!frequency) found.frequency = 'Select Frequency';
    setProblems(found);
    if (Object.keys(found).length) return;
    try {
      const task = await schedule.mutateAsync({
        savedReportId: saved.id,
        name,
        frequency: frequency as ScheduleTaskInput['frequency'],
      });
      notify.success(`"${task.name}" scheduled. Its files are listed under Scheduled reports.`);
      onClose();
    } catch (err) {
      const fromServer = fieldErrors(err);
      if (Object.keys(fromServer).length) setProblems(fromServer);
      else notify.error(err);
    }
  };

  return (
    <FormDialog
      open
      title="Schedule Task"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={schedule.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <TextField
          required
          autoFocus
          label="Report Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={Boolean(problems.name)}
          helperText={problems.name}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          select
          required
          label="Frequency"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          error={Boolean(problems.frequency)}
          helperText={
            problems.frequency ??
            `Each file covers "${saved.datePreset}". The first one is produced within a minute.`
          }
        >
          {SCHEDULE_FREQUENCIES.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </FormDialog>
  );
}

/** Saved report settings (PC520), "Saved Parameters": run, schedule or delete a saved selection. */
export function SavedReportsPage() {
  const { can } = useAuth();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({ name: '', reportKey: '' });
  const [applied, setApplied] = useState<Query>({});
  const [scheduling, setScheduling] = useState<SavedReportDto | null>(null);
  const [deleting, setDeleting] = useState<SavedReportDto | null>(null);
  const saved = useSaved(applied);
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/custom-reports/saved/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['custom-reports'] }),
  });

  const columns: Column<SavedReportDto>[] = [
    { key: '#', header: '#', width: 50, render: (_s, i) => i + 1 },
    { key: 'name', header: 'Report Name', render: (s) => s.name },
    { key: 'report', header: 'Related Report', render: (s) => s.reportName },
    { key: 'viewType', header: 'View type', render: (s) => s.viewType },
    { key: 'datePreset', header: 'Schedule date', render: (s) => s.datePreset },
    { key: 'createdBy', header: 'Saved By', render: (s) => s.createdBy?.loginId ?? '' },
    {
      key: 'createdAt',
      header: 'Saved On',
      nowrap: true,
      render: (s) => formatDateTime(s.createdAt),
    },
    {
      key: 'action',
      header: 'Action',
      nowrap: true,
      render: (s) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            component="a"
            href={api.url(`/custom-reports/saved/${s.id}/run`)}
            download
            aria-label={`Run ${s.name}`}
          >
            Run Report
          </Button>
          {can('PC527') ? (
            <Button size="small" onClick={() => setScheduling(s)} aria-label={`Schedule ${s.name}`}>
              Schedule Task
            </Button>
          ) : null}
          {s.mine ? (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => setDeleting(s)}
                aria-label={`Delete ${s.name}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader title="Saved Parameters" code="PC520" />
      <Paper
        variant="outlined"
        component="form"
        noValidate
        aria-label="Saved Parameters filters"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setApplied({ name: draft.name.trim(), reportKey: draft.reportKey });
        }}
        sx={{
          p: 1.5,
          mb: 1.5,
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          alignItems: 'start',
        }}
      >
        <TextField
          label="Report Name"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          select
          label="Related Report"
          value={draft.reportKey}
          onChange={(e) => setDraft((d) => ({ ...d, reportKey: e.target.value }))}
        >
          <MenuItem value="">All</MenuItem>
          {CUSTOM_REPORTS.map((r) => (
            <MenuItem key={r.key} value={r.key}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 37 }}>
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </Box>
      </Paper>
      <DataTable
        label="Saved Parameters"
        columns={columns}
        rows={saved.data}
        rowKey={(s) => s.id}
        loading={saved.isFetching}
        error={saved.error}
        onRetry={() => void saved.refetch()}
        emptyText="No record found."
      />
      {scheduling ? (
        <ScheduleDialog saved={scheduling} onClose={() => setScheduling(null)} />
      ) : null}
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete saved parameters"
        message={`Delete "${deleting?.name ?? ''}"? Reports already downloaded are not affected.`}
        confirmLabel="Delete"
        danger
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate(deleting.id, {
            onSuccess: () => {
              notify.success(`"${deleting.name}" deleted.`);
              setDeleting(null);
            },
            onError: (err) => {
              notify.error(err);
              setDeleting(null);
            },
          });
        }}
      />
    </Box>
  );
}

/* ------------------------------ Scheduled reports -------------------------- */

/** The files a task has produced, newest first. */
function FilesDialog({ task, onClose }: { task: ScheduledTaskDto; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Files of {task.name}</DialogTitle>
      <DialogContent dividers>
        <DataTable
          label={`Files of ${task.name}`}
          columns={[
            {
              key: 'generatedAt',
              header: 'Produced On',
              nowrap: true,
              render: (r) => formatDateTime(r.generatedAt),
            },
            { key: 'status', header: 'Status', render: (r) => r.status },
            { key: 'rows', header: 'Records', align: 'right', render: (r) => r.rows },
            {
              key: 'file',
              header: 'File',
              render: (r) =>
                r.status === 'Success' ? (
                  <MuiLink
                    href={api.url(`/custom-reports/scheduled/${task.id}/runs/${r.id}`)}
                    download
                  >
                    Download CSV
                  </MuiLink>
                ) : (
                  r.message
                ),
            },
          ]}
          rows={task.runs}
          rowKey={(r) => r.id}
          emptyText="No file has been produced yet."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Scheduled reports (PC527), "Schedule Task": the tasks and the files they produced. */
export function ScheduledReportsPage() {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<ScheduledTaskDto | null>(null);
  const [deleting, setDeleting] = useState<ScheduledTaskDto | null>(null);
  const tasks = useQuery({
    queryKey: ['custom-reports', 'scheduled'],
    queryFn: () => api.get<{ data: ScheduledTaskDto[] }>('/custom-reports/scheduled'),
    select: (r) => r.data,
    // Files appear without a reload once the scheduler has produced them.
    refetchInterval: 60_000,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/custom-reports/scheduled/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['custom-reports'] }),
  });

  const columns: Column<ScheduledTaskDto>[] = [
    { key: '#', header: '#', width: 50, render: (_t, i) => i + 1 },
    { key: 'name', header: 'Report Name', render: (t) => t.name },
    { key: 'saved', header: 'Saved Parameters', render: (t) => t.savedReportName },
    { key: 'report', header: 'Related Report', render: (t) => t.reportName },
    { key: 'frequency', header: 'Frequency', render: (t) => t.frequency },
    {
      key: 'lastRunAt',
      header: 'Last Run',
      nowrap: true,
      render: (t) => (t.lastRunAt ? formatDateTime(t.lastRunAt) : ''),
    },
    {
      key: 'nextRunAt',
      header: 'Next Run',
      nowrap: true,
      render: (t) => formatDateTime(t.nextRunAt),
    },
    { key: 'createdBy', header: 'Scheduled By', render: (t) => t.createdBy?.loginId ?? '' },
    {
      key: 'files',
      header: 'Files',
      render: (t) => (
        <Button size="small" onClick={() => setFiles(t)} aria-label={`Files of ${t.name}`}>
          {t.runs.length} file{t.runs.length === 1 ? '' : 's'}
        </Button>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (t) =>
        t.mine ? (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleting(t)} aria-label={`Delete ${t.name}`}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Box>
      <PageHeader title="Schedule Task" code="PC527" />
      <DataTable
        label="Schedule Task"
        columns={columns}
        rows={tasks.data}
        rowKey={(t) => t.id}
        loading={tasks.isLoading}
        error={tasks.error}
        onRetry={() => void tasks.refetch()}
        emptyText="No record found. Schedule a report from Saved report settings."
      />
      {files ? <FilesDialog task={files} onClose={() => setFiles(null)} /> : null}
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete scheduled task"
        message={`Delete "${deleting?.name ?? ''}" and the files it produced?`}
        confirmLabel="Delete"
        danger
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate(deleting.id, {
            onSuccess: () => {
              notify.success(`"${deleting.name}" deleted.`);
              setDeleting(null);
            },
            onError: (err) => {
              notify.error(err);
              setDeleting(null);
            },
          });
        }}
      />
    </Box>
  );
}
